import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';

// dir="auto": the browser takes the direction from the text itself, so a channel name in Arabic
// or Hebrew reads right to left inside a left-to-right screen (C3 R7). A constant, not a text.
const OWN_DIRECTION = 'auto';

/** Props of {@link SectionHeader}. */
export interface SectionHeaderProps {
  /** Heading text, already translated (I12). */
  readonly title: string;
  /** Link or button on the right ("Manage", "See all"); the screen brings its router link. */
  readonly action?: ReactNode;
  readonly id?: string | undefined;
}

/**
 * Title of a Home section with an optional link on the right, as "My channels · Manage" and
 * "Open positions · See all" on mockup 1. A real `h2`, so screen readers can jump between sections.
 *
 * @example <SectionHeader title={t('home.channels.title')} action={<Link …>{t('home.channels.manage')}</Link>} />
 */
export function SectionHeader({ title, action, id }: SectionHeaderProps): ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 2,
        px: 0.5,
      }}
    >
      {/* R7: a long title is cut visually with an ellipsis (noWrap) but stays whole in the DOM,
          so the heading's accessible name is complete; minWidth 0 lets it shrink in the flex row
          and the action keeps its place from 320 px. */}
      {/* dir="auto": a channel name in Arabic or Hebrew keeps its own direction. */}
      <Typography variant="h2" id={id} dir={OWN_DIRECTION} noWrap sx={{ minWidth: 0 }}>
        {title}
      </Typography>
      {action !== undefined && (
        <Box sx={{ fontSize: 14, fontWeight: 500, flexShrink: 0 }}>{action}</Box>
      )}
    </Box>
  );
}
